import { KeyboardEvent, PointerEvent, useCallback, useRef } from "react";

import { RIGHT_SIDEBAR_RESIZE_KEYBOARD_STEP_PX, RIGHT_SIDEBAR_WIDTH_CLASS } from "@/constants/ai-chat";

import { clampRightSidebarWidthPx, toRightSidebarCssWidth } from "../utils/right-sidebar-width.utils";

const RESIZING_ATTRIBUTE = "data-resizing";

/**
 * Drag (or arrow keys) on the panel's left edge to resize the Darwin panel. While dragging, the
 * width is written straight to the CSS variable so the app does not re-render on every move;
 * the final width is reported once at the end.
 */
export function useRightSidebarResize({ onWidthChange }: { onWidthChange: (widthPx: number | null) => void }) {
    const dragRef = useRef<{ startX: number; startWidth: number; container: HTMLElement; width: number } | null>(null);

    const getContainer = (element: HTMLElement) => element.closest<HTMLElement>(`.${RIGHT_SIDEBAR_WIDTH_CLASS}`);

    const getCurrentWidth = (container: HTMLElement) => {
        const panel = container.querySelector<HTMLElement>("[data-side='right']") ?? container;
        return panel.getBoundingClientRect().width;
    };

    const handlePointerDown = useCallback((event: PointerEvent<HTMLElement>) => {
        const container = getContainer(event.currentTarget);
        if (!container || event.button !== 0) return;

        event.preventDefault();
        event.currentTarget.setPointerCapture(event.pointerId);
        const startWidth = getCurrentWidth(container);
        dragRef.current = { startX: event.clientX, startWidth, container, width: startWidth };
        container.setAttribute(RESIZING_ATTRIBUTE, "true");
    }, []);

    const handlePointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
        const drag = dragRef.current;
        if (!drag) return;

        // The panel sits on the right, so dragging its left edge to the left makes it wider
        drag.width = clampRightSidebarWidthPx(drag.startWidth + drag.startX - event.clientX);
        drag.container.style.setProperty("--sidebar-width", toRightSidebarCssWidth(drag.width));
    }, []);

    const handlePointerUp = useCallback(
        (event: PointerEvent<HTMLElement>) => {
            const drag = dragRef.current;
            if (!drag) return;

            dragRef.current = null;
            event.currentTarget.releasePointerCapture(event.pointerId);
            drag.container.removeAttribute(RESIZING_ATTRIBUTE);
            onWidthChange(drag.width);
        },
        [onWidthChange]
    );

    const handleKeyDown = useCallback(
        (event: KeyboardEvent<HTMLElement>) => {
            const container = getContainer(event.currentTarget);
            if (!container) return;

            const width = getCurrentWidth(container);
            const keyActions: Record<string, () => void> = {
                ArrowLeft: () => onWidthChange(clampRightSidebarWidthPx(width + RIGHT_SIDEBAR_RESIZE_KEYBOARD_STEP_PX)),
                ArrowRight: () =>
                    onWidthChange(clampRightSidebarWidthPx(width - RIGHT_SIDEBAR_RESIZE_KEYBOARD_STEP_PX)),
                Home: () => onWidthChange(null),
            };
            const action = keyActions[event.key];
            if (!action) return;

            event.preventDefault();
            action();
        },
        [onWidthChange]
    );

    const handleDoubleClick = useCallback(() => onWidthChange(null), [onWidthChange]);

    return {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerUp,
        onKeyDown: handleKeyDown,
        onDoubleClick: handleDoubleClick,
    };
}
